import { useState } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { KeyRound, ShieldCheck } from "lucide-react"
import { ZweiFaktorDialog, type ZweiFaktorGeheimnis } from "@/components/zugaenge/ZweiFaktorDialog"

/**
 * Eigenes Konto: Name, Rolle, Rechte, Passwort und zweiter Faktor (#27).
 *
 * Ausgelöst durch das Onboarding von Robin (Werkstudent, Start September 2026).
 * Bis hierher konnte **niemand** sein Passwort ändern — es gab kein Formular
 * und keine Route. Was einmal gesetzt war, blieb, auch wenn es über einen
 * unsicheren Weg weitergegeben wurde.
 *
 * Seit dem 25.08.2026 steht hier auch der zweite Faktor. 🔑 Wer ihn von der
 * Verwaltung bekommt, teilt sein Geheimnis mit dem, der ihn ausgestellt hat —
 * einmal selbst erneuern, und es kennt wieder nur einer.
 */

type Konto = {
  benutzer: string
  rolle: string
  rolle_name?: string
  rechte?: string[]
  in_datenbank?: boolean
  hat_2fa?: boolean
  min_laenge: number
}

// Nur zum Anzeigen. Was ein Recht wirklich sperrt, steht im Katalog
// (`core/rechte.py`) und wird im Backend geprüft — hier soll man nur sehen,
// warum ein Menüpunkt fehlt.
const RECHT_TEXT: Record<string, string> = {
  kosten: "Kosten",
  sicherheit: "Sicherheit",
  mandanten: "Mandanten ändern",
  zugaenge: "Zugänge und Rollen",
  mails_senden: "Kunden anschreiben (E-Mail, WhatsApp)",
}

export default function KontoPage() {
  const [alt, setAlt] = useState("")
  const [neu, setNeu] = useState("")
  const [wdh, setWdh] = useState("")
  const [zweiFaPass, setZweiFaPass] = useState("")
  const [geheimnis, setGeheimnis] = useState<ZweiFaktorGeheimnis | null>(null)
  const [meldung, setMeldung] = useState<{ art: "ok" | "fehler"; text: string } | null>(null)

  const konto = useQuery<Konto>({
    queryKey: ["konto"],
    queryFn: async () => {
      const r = await fetch("/api/v1/konto", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const minLaenge = konto.data?.min_laenge ?? 12

  const wechseln = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/konto/passwort", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ alt, neu }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d
    },
    onSuccess: () => {
      setAlt(""); setNeu(""); setWdh("")
      setMeldung({ art: "ok", text: "Passwort geändert." })
    },
    onError: (e) => setMeldung({ art: "fehler", text: String(e instanceof Error ? e.message : e) }),
  })

  const zweiFa = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/v1/konto/2fa", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ passwort: zweiFaPass }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      return d as ZweiFaktorGeheimnis
    },
    onSuccess: (d) => {
      setZweiFaPass("")
      setGeheimnis(d)
      setMeldung(null)
      konto.refetch()
    },
    onError: (e) => setMeldung({ art: "fehler", text: String(e instanceof Error ? e.message : e) }),
  })

  // Die Prüfungen hier sind Höflichkeit, nicht Sicherheit — dieselben Regeln
  // stehen im Backend, und nur die dort zählen.
  const zuKurz = neu.length > 0 && neu.length < minLaenge
  const ungleich = wdh.length > 0 && neu !== wdh
  const bereit = alt.length > 0 && neu.length >= minLaenge && neu === wdh && !wechseln.isPending

  const feld = {
    width: "100%", fontSize: 13, padding: "7px 9px", borderRadius: 6,
    border: "1px solid var(--border-color)", background: "var(--bg-card)",
    color: "var(--text)",
  } as const

  return (
    <div style={{ padding: "20px 24px", maxWidth: 460 }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 14 }}>Mein Konto</h1>

      <div style={{
        fontSize: 13, marginBottom: 20, padding: 12, borderRadius: 8,
        border: "1px solid var(--border-color)", background: "var(--bg-card)",
      }}>
        <div><strong>{konto.data?.benutzer || "—"}</strong></div>
        <div style={{ color: "var(--text-muted)", marginTop: 3 }}>
          {konto.data?.rolle_name || konto.data?.rolle || "—"}
        </div>
        <div style={{ color: "var(--text-muted)", marginTop: 6, fontSize: 12 }}>
          {konto.data?.rechte?.length
            ? <>Zusätzlich freigeschaltet: {konto.data.rechte
                .map((r) => RECHT_TEXT[r] || r).join(", ")}.</>
            : <>Keine gesonderten Rechte — die tägliche Arbeit, ohne Kosten,
              Sicherheit, Mandanten und Zugänge.</>}
        </div>
        <div style={{ color: "var(--text-muted)", marginTop: 4, fontSize: 12 }}>
          Zweiter Faktor: {konto.data?.hat_2fa ? "eingerichtet" : "nicht eingerichtet"}
        </div>
      </div>

      <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Passwort ändern</h2>

      <form
        onSubmit={(e) => { e.preventDefault(); setMeldung(null); if (bereit) wechseln.mutate() }}
        style={{ display: "flex", flexDirection: "column", gap: 9 }}
      >
        <label style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Aktuelles Passwort
          <input type="password" value={alt} onChange={(e) => setAlt(e.target.value)}
                 autoComplete="current-password" style={{ ...feld, marginTop: 3 }} />
        </label>
        <label style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Neues Passwort · mindestens {minLaenge} Zeichen
          <input type="password" value={neu} onChange={(e) => setNeu(e.target.value)}
                 autoComplete="new-password" style={{ ...feld, marginTop: 3 }} />
        </label>
        {zuKurz && (
          <div style={{ fontSize: 12, color: "var(--destructive)" }}>
            Noch {minLaenge - neu.length} Zeichen. Länge hilft mehr als Sonderzeichen.
          </div>
        )}
        <label style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Neues Passwort wiederholen
          <input type="password" value={wdh} onChange={(e) => setWdh(e.target.value)}
                 autoComplete="new-password" style={{ ...feld, marginTop: 3 }} />
        </label>
        {ungleich && (
          <div style={{ fontSize: 12, color: "var(--destructive)" }}>
            Die beiden Eingaben sind nicht gleich.
          </div>
        )}
        <button
          type="submit"
          disabled={!bereit}
          style={{
            marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6,
            alignSelf: "flex-start", fontSize: 13, fontWeight: 500,
            padding: "7px 14px", borderRadius: 6, border: "none",
            background: bereit ? "#3C3489" : "rgba(44,44,42,0.12)",
            color: bereit ? "#fff" : "var(--text-muted)",
            cursor: bereit ? "pointer" : "not-allowed",
          }}
        >
          <KeyRound size={14} /> {wechseln.isPending ? "Speichert …" : "Ändern"}
        </button>
      </form>

      {konto.data?.in_datenbank !== false && (
        <>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: "26px 0 6px" }}>
            Zweiter Faktor {konto.data?.hat_2fa ? "erneuern" : "einrichten"}
          </h2>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
            Erzeugt einen neuen Schlüssel für die Authenticator-App und zeigt ihn
            einmal an. {konto.data?.hat_2fa && "Der bisherige Code gilt danach nicht mehr."}
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); setMeldung(null); if (zweiFaPass) zweiFa.mutate() }}
            style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
          >
            {/* 🔴 Nur mit dem aktuellen Passwort — sonst übernimmt jede gekaperte
                Sitzung den zweiten Faktor und damit dauerhaft das Konto. */}
            <input type="password" value={zweiFaPass} onChange={(e) => setZweiFaPass(e.target.value)}
                   autoComplete="current-password" placeholder="Aktuelles Passwort"
                   style={{ ...feld, width: 220 }} />
            <button
              type="submit"
              disabled={!zweiFaPass || zweiFa.isPending}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13,
                fontWeight: 500, padding: "7px 14px", borderRadius: 6, border: "none",
                background: zweiFaPass ? "#3C3489" : "rgba(44,44,42,0.12)",
                color: zweiFaPass ? "#fff" : "var(--text-muted)",
                cursor: zweiFaPass ? "pointer" : "not-allowed",
              }}
            >
              <ShieldCheck size={14} /> {zweiFa.isPending ? "Erzeugt …" : "Neuen Schlüssel erzeugen"}
            </button>
          </form>
        </>
      )}

      {meldung && (
        <div style={{
          marginTop: 12, fontSize: 12.5,
          color: meldung.art === "ok" ? "#059669" : "var(--destructive)",
        }}>
          {meldung.text}
        </div>
      )}

      {geheimnis && (
        <ZweiFaktorDialog daten={geheimnis} onClose={() => setGeheimnis(null)} />
      )}
    </div>
  )
}
